/**
 * Whether a horizontal scroller should consume a wheel event to pan itself.
 *
 * Split out of `GradientScrollDiv` and kept free of React, DOM types and SCSS so the standalone
 * test runner can import it (`npx tsx`). The decision is the whole point of the file: a handler
 * that answered "yes" unconditionally shipped, trapped the page's vertical scroll over every
 * horizontal row, and was removed in `3b1f8007`. The bounds below are what let it come back.
 */

/** Below this, treat the row as fully scrolled — sub-pixel layout leaves a fraction behind. */
export const SCROLL_EPSILON_PX = 1;

export type WheelDelta = { deltaX: number; deltaY: number };

export type ScrollerGeometry = { scrollLeft: number; scrollWidth: number; clientWidth: number };

export function shouldPanHorizontally(wheel: WheelDelta, geometry: ScrollerGeometry): boolean {
  // A real horizontal gesture is already going where it should; consuming it too would move
  // the row twice as far as the finger.
  if (wheel.deltaX !== 0 || wheel.deltaY === 0) return false;

  const maxScroll = geometry.scrollWidth - geometry.clientWidth;
  if (maxScroll <= SCROLL_EPSILON_PX) return false;

  // At either end the wheel belongs to the page. This is the case whose absence made the
  // original handler a scroll trap.
  if (wheel.deltaY < 0 && geometry.scrollLeft <= SCROLL_EPSILON_PX) return false;
  if (wheel.deltaY > 0 && geometry.scrollLeft >= maxScroll - SCROLL_EPSILON_PX) return false;

  return true;
}
